// material-sourcing.ts — material supply referential: distributor and brand per material, backing the
// supplier / brand columns of the input screen. Indicative only; this data never enters compute().
// Source: the `material_suppliers` table.

/** Referential supply of one material (its default distributor and brand). */
export interface MaterialSupply {
  /** Distributor UPSIZE orders the material from. */
  supplier?: string;
  /** Brand (manufacturer), paired with the distributor. */
  brand?: string;
  /** Supplier product ref (dropdown tooltip), when the referential carries it. */
  supplierProductRef?: string;
}

/** Supply referential: supply data per material ref. */
export type MaterialSupplyRef = Record<string, MaterialSupply>;

/**
 * Default supply of a material, or {} when nothing is referenced.
 * @param ref The supply referential.
 * @param material The material reference.
 * @returns The material's supply data.
 */
export function defaultSupply(ref: MaterialSupplyRef, material: string | undefined): MaterialSupply {
  return (material ? ref[material] : undefined) ?? {};
}

/**
 * Dropdown options derived from the referential: distributors, plus brands per distributor.
 * @param ref The supply referential.
 * @returns The sorted distributors and the sorted brands of each distributor.
 */
export function supplyOptions(ref: MaterialSupplyRef): { distributors: string[]; brandsBySupplier: Record<string, string[]> } {
  const distributors = new Set<string>();
  const bySupplier: Record<string, Set<string>> = {};
  for (const entry of Object.values(ref)) {
    const supplier = String(entry?.supplier ?? "").trim();
    const brand = String(entry?.brand ?? "").trim();
    if (!supplier) continue;
    distributors.add(supplier);
    (bySupplier[supplier] ??= new Set<string>());
    if (brand) bySupplier[supplier].add(brand);
  }
  const brandsBySupplier: Record<string, string[]> = {};
  for (const [supplier, brands] of Object.entries(bySupplier)) brandsBySupplier[supplier] = [...brands].sort((a, b) => a.localeCompare(b, "fr"));
  return { distributors: [...distributors].sort((a, b) => a.localeCompare(b, "fr")), brandsBySupplier };
}
